import React from "react";
import { motion } from "framer-motion";
import { ScanLine, Sparkles, Crown, Camera, CalendarClock, Scale, NotebookPen, Download, ChevronRight, Plus } from "lucide-react";
import { DOC_SECTIONS } from "./constants";

const TILE = "relative rounded-[28px] p-4 sm:p-5 bg-white border border-[#F0EBE3] overflow-hidden text-left w-full";
const hover = { whileHover: { y: -3 }, whileTap: { scale: 0.97 } };

export function AiScanTile({ isPro, onStart }) {
  return (
    <motion.button {...hover} onClick={onStart} data-testid="ai-scan-tile"
      className="relative rounded-[28px] p-4 sm:p-5 overflow-hidden text-left w-full h-full min-h-[190px] flex flex-col"
      style={{
        background: "radial-gradient(120% 90% at 100% 0%, rgba(255,176,32,0.35) 0%, rgba(255,176,32,0) 55%), linear-gradient(150deg,#16120F 0%,#2A1810 60%,#3A1F0E 100%)",
        boxShadow: "0 22px 40px -20px rgba(255,122,26,0.6)",
      }}>
      <motion.div aria-hidden animate={{ y: ["-10%", "110%"] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", repeatType: "reverse" }}
        className="absolute inset-x-4 h-px pointer-events-none" style={{ background: "linear-gradient(90deg,transparent,#F4B83C,transparent)", boxShadow: "0 0 18px 4px rgba(244,184,60,0.5)" }} />
      <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FF7A1A,#F4B83C)" }}>
        <ScanLine className="h-5 w-5 text-white" />
      </div>
      <div className="mt-auto">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.16em]"
          style={isPro ? { background: "rgba(52,211,153,0.16)", color: "#34D399" } : { background: "#F4B83C", color: "#1A1A1A" }}>
          {isPro ? <><Sparkles className="h-2.5 w-2.5" /> AI attiva</> : <><Crown className="h-2.5 w-2.5" /> Pro</>}
        </span>
        <div className="font-display text-[19px] leading-tight text-white mt-1.5">Scansiona con ORIO AI</div>
        <div className="text-[11.5px] text-white/65 mt-0.5 leading-snug">Vaccini, ricette, fatture: compila tutto da sola</div>
        <div className="mt-2.5 inline-flex items-center gap-1 text-[12px] font-semibold text-[#F4B83C]">
          <Camera className="h-3.5 w-3.5" /> {isPro ? "Scatta ora" : "Sblocca con Pro"}
        </div>
      </div>
    </motion.button>
  );
}

export function ScadenzeTile({ next, activeCount, onOpen }) {
  const urgent = next && next.days <= 7;
  const color = !next ? "#2DBA60" : next.days < 0 ? "#E94545" : urgent ? "#F59E0B" : "#2DBA60";
  return (
    <motion.button {...hover} onClick={onOpen} data-testid="scadenze-reminders-tile" className={`${TILE} h-full min-h-[190px] flex flex-col`}>
      <div className="flex items-center justify-between">
        <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: `${color}1A` }}>
          <CalendarClock className="h-5 w-5" style={{ color }} />
        </div>
        {activeCount > 0 && <span className="px-2 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ background: color }}>{activeCount}</span>}
      </div>
      <div className="mt-auto">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] font-semibold">Scadenze</div>
        {next ? (
          <>
            <div className="font-display text-[18px] leading-tight text-[#1A1A1A] mt-1 line-clamp-2">{next.title}</div>
            <div className="text-[12px] font-semibold mt-1" style={{ color }}>
              {next.days < 0 ? `Scaduto da ${-next.days}gg` : next.days === 0 ? "Oggi" : next.days === 1 ? "Domani" : `Tra ${next.days} giorni`}
            </div>
          </>
        ) : (
          <>
            <div className="font-display text-[18px] leading-tight text-[#1A1A1A] mt-1">Tutto in regola</div>
            <div className="text-[12px] text-[#9A9A9A] mt-1 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Aggiungi promemoria</div>
          </>
        )}
      </div>
    </motion.button>
  );
}

function Spark({ values }) {
  if (values.length < 2) return null;
  const min = Math.min(...values); const range = Math.max(0.5, Math.max(...values) - min);
  const d = values.map((v, i) => `${i ? "L" : "M"}${(i / (values.length - 1)) * 100},${36 - ((v - min) / range) * 30}`).join(" ");
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-10">
      <motion.path d={d} fill="none" stroke="#0EA5E9" strokeWidth="2.5" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1 }} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function WeightTile({ entries, fallbackKg, onOpen }) {
  const values = entries.map((e) => Number(e.value));
  const latest = values.length ? values[values.length - 1] : fallbackKg;
  return (
    <motion.button {...hover} onClick={onOpen} data-testid="weight-sparkline-tile" className={`${TILE} min-h-[150px] flex flex-col`}>
      <div className="flex items-center gap-2">
        <span className="h-9 w-9 rounded-xl bg-[#0EA5E9]/10 flex items-center justify-center"><Scale className="h-4 w-4 text-[#0EA5E9]" /></span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] font-semibold">Peso</span>
      </div>
      <div className="mt-2 font-display text-3xl text-[#1A1A1A] tabular-nums">{latest ? `${latest} kg` : "—"}</div>
      <div className="mt-auto">{values.length > 1 ? <Spark values={values} /> : <div className="text-[11.5px] text-[#9A9A9A]">Registra il peso dal diario</div>}</div>
    </motion.button>
  );
}

export function SimpleTile({ icon: Icon, color, kicker, title, sub, onClick, testid }) {
  return (
    <motion.button {...hover} onClick={onClick} data-testid={testid} className={`${TILE} min-h-[150px] flex flex-col`}>
      <span className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: `${color}1A` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </span>
      <div className="mt-auto">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] font-semibold">{kicker}</div>
        <div className="font-display text-[17px] leading-tight text-[#1A1A1A] mt-0.5">{title}</div>
        {sub && <div className="text-[11.5px] text-[#9A9A9A] mt-0.5">{sub}</div>}
      </div>
    </motion.button>
  );
}

export const DiaryTile = (p) => <SimpleTile icon={NotebookPen} color="#B37016" kicker="Diario" title="Momenti & salute" sub="Note, sintomi, peso" testid="diary-tile" {...p} />;
export const PassportTile = (p) => <SimpleTile icon={Download} color="#2DBA60" kicker="Passaporto" title="Esporta PDF" sub="Tutti i dati in un file" testid="export-passport-pdf-button" {...p} />;

export function DocSectionsGallery({ counts, onOpen }) {
  return (
    <div className="-mx-4 sm:mx-0 px-4 sm:px-0 flex sm:grid sm:grid-cols-3 gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2" data-testid="doc-sections-gallery">
      {DOC_SECTIONS.map((s, i) => {
        const c = counts[s.id] || { total: 0, expiring: 0 };
        return (
          <motion.button key={s.id} {...hover} onClick={() => onOpen(s.id)} data-testid={`doc-tile-${s.id}`}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i, type: "spring", stiffness: 300, damping: 26 }}
            className="snap-start shrink-0 w-[150px] sm:w-auto aspect-square rounded-[28px] p-4 border text-left flex flex-col relative overflow-hidden"
            style={{ background: `linear-gradient(160deg, ${s.color}14 0%, #FFFFFF 70%)`, borderColor: `${s.color}2E` }}>
            <div className="flex items-start justify-between">
              <span className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: s.color }}>
                <s.icon className="h-5 w-5 text-white" />
              </span>
              {c.expiring > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white bg-[#E94545]">{c.expiring} !</span>}
            </div>
            <div className="mt-auto">
              <div className="font-display text-[34px] leading-none text-[#1A1A1A] tabular-nums">{c.total}</div>
              <div className="text-[13.5px] font-semibold text-[#1A1A1A] mt-1 leading-tight">{s.title}</div>
              <div className="text-[10.5px] text-[#9A9A9A] leading-tight truncate">{s.sub}</div>
            </div>
            <ChevronRight className="absolute right-3 bottom-3 h-4 w-4 text-[#C7C0B3]" />
          </motion.button>
        );
      })}
    </div>
  );
}
